import dataProvider from "src/dataProvider";

const fetcher = dataProvider("adminInstance");

/**
 * Submit or update DPR form data
 * Handles: Site Characteristics, Interventions & Zoning, Implementation Timeline, Declarations
 */
export const submitDpr = async (data: SubmitDprPayload) => {
  return fetcher.post<SubmitDprPayload, any>("/dpr/submit", data);
};

/**
 * Get DPR data by land ownership EPID
 */
export const getDprByEpid = async (epid: string) => {
  return fetcher.get<any>(`/dpr/epid/${epid}`);
};

/**
 * Get targets grouped by year for a land ownership
 */
export const getDprTargets = async (landOwnershipEpid: string) => {
  return fetcher.get<any>(`/dpr/targets/${landOwnershipEpid}`);
};

/**
 * Get distinct unit values from the targets table
 */
export const getDistinctUnits = async () => {
  return fetcher.get<string[]>(`/dpr/units`);
};

/**
 * Review DPR — DNO/IO or SNO approves or raises a query
 */
export const reviewDpr = async (data: DprReviewPayload) => {
  return fetcher.post<DprReviewPayload, any>("/dpr/review", data);
};

/**
 * Deselect the land parcel (admin only) — releases it back to the pool
 * and moves the DPR to DPR_DESELECT
 */
export const deselectDpr = async (data: DprDeselectPayload) => {
  return fetcher.post<DprDeselectPayload, any>("/dpr/deselect", data);
};

/**
 * Get all DPR queries for a given EPID
 */
export const getDprQueries = async (epid: string) => {
  return fetcher.get<any>(`/dpr/query/${epid}`);
};

// ==================== Payload Types ====================

export interface TreeSpeciesItem {
  botanicalName: string;
  commonName?: string;
  seedlings: number;
  spacing: string;
  nurseryDetails?: string;
}

export interface DprTargetEntry {
  indicator: string;
  activity: string;
  unit?: string;
  numberOfUnits?: number;
  costPerUnit?: number;
  costRequired?: number;
  description?: string;
}

export interface DprYearTargets {
  year: string; // '1st Year', '2nd Year', etc.
  targets: DprTargetEntry[];
}

export interface SiteCharacteristicsPayload {
  legalStatusOfLand?: string;
  terrain?: string;
  slope?: string;
  climaticZone?: string;
  soilType?: string;
  soilDepthAndTexture?: string;
  soilMoistureRegime?: string;
  soilpH?: string;
  sourceOfWater?: string;
  waterSupplyLines?: string;
  distanceToNearestWaterSource?: string;
  typeOfVegetation?: string;
  dominantSpecies?: string;
  treeCanopyDensity?: number;
  presenceOfInvasiveSpecies?: string;
  invasiveSpeciesDetails?: string;
  existingFauna?: string;
  proximityToLocalCommunities?: string;
  rightsAndUsage?: string;
  communityDependency?: string;
  anyExistingRestrictions?: string;
  accessibility?: string;
  // New DPR fields
  circleName?: string;
  nearestRoadType?: string;
  approachRoute?: string;
  inspectionPaths?: string;
  soilFertilityStatus?: string;
  suitabilityForConservation?: string;
  treesGirth0To10?: number;
  treesGirth10To30?: number;
  treesGirth30To60?: number;
  treesGirthGreater60?: number;
}

export interface InterventionPayload {
  projectDetailId: string;
  interventionType?: string;
  kmlFileId?: string;
  kmlFileName?: string;
  treePlantationArea?: number;
  treePlantationSpecies?: string;
  seedlingsPerHectare?: number;
  spacingPattern?: string;
  nurserySource?: string;
  nurseryLocation?: string;
  temporaryNurseryDetails?: string;
  maintenancePlan?: string;
  treeSpecies?: TreeSpeciesItem[];
  soilConservationArea?: number;
  conservationWorkType?: string;
  conservationWorkZone?: string;
  conservationWorkPurpose?: string;
  waterSupplyArea?: number;
  proposedWaterSource?: string;
  supplyLineLayout?: string;
  waterHarvestingStructures?: string;
  protectionArea?: number;
  fencingType?: string;
  fencingLength?: number;
  firePreventionWorks?: string;
  watchWardArrangements?: string;
  biodiversityArea?: number;
  pollinatorSpecies?: string;
  ntfpSpecies?: string;
  grassesShrubsMedicinal?: string;
  // DPR Purpose & GC Utilisation
  dprPurpose?: string[];
  estimatedGreenCredits?: number;
  gcUtilisationDescription?: string;
}

export interface DeclarationsPayload {
  declarationTrueComplete?: boolean;
  declarationAbideRules?: boolean;
  declarationAdequateResources?: boolean;
  declarationMaintenanceFund?: boolean;
  declarationPerformanceParameters?: boolean;
  declarationProgressReports?: boolean;
  declarationNoLandClaim?: boolean;
  declarationNoCommercialActivity?: boolean;
}

export interface SubmitDprPayload {
  projectDetailId: string;
  landOwnershipEpid: string;
  status?: string;
  landArea?: number;
  siteCharacteristics?: SiteCharacteristicsPayload;
  intervention?: InterventionPayload;
  yearwiseTargets?: DprYearTargets[];
  declarations?: DeclarationsPayload;
}

export interface DprReviewPayload {
  epid: string;
  action: "APPROVE" | "QUERY" | "REJECT";
  query?: string;
  reason?: string;
}

export interface DprDeselectPayload {
  epid: string;
  reason?: string;
  fileIds?: string[];
}
